import {
  useWatchableValue,
  Watchable,
} from '@cotera/client/v0/actions/framework';
import type { Agent } from '@cotera/sdk/core';
import {
  resolveAgentDisplayMeta,
  type AgentNodeLiveMeta,
} from './components/node/agent-node-display';
import { useAgentLiveMeta } from './use-agent-live-meta';
import {
  useAgentNodeConfig,
  type AgentNodeConfig,
} from './use-agent-node-config';
import { useWorkflowAgentNodeModelFromViewModel } from './use-workflow-agent-node-model';
import { agentFromNodeConfig } from './workflow-node-models/workflow-agent-node.model';
import type { WorkflowGraphNodeViewModelHandle } from './workflow-render-catalog';

const NULL_AGENT = Watchable.fromValue<Agent | null>(null);

export type WorkflowAgentNodeState = {
  agent: Agent | null;
  config: AgentNodeConfig;
  meta: AgentNodeLiveMeta | undefined;
};

/**
 * Live agent chrome for workflow graph surfaces. Config (tools, input shape,
 * editor state) always loads via {@link useAgentNodeConfig} keyed on agent id.
 * The SDK `Agent` is taken from the node model when present, otherwise derived
 * from config once it hydrates.
 */
export function useWorkflowAgentNodeState(
  agentId: string | undefined,
  viewModel: WorkflowGraphNodeViewModelHandle | null = null
): WorkflowAgentNodeState {
  const nodeModel = useWorkflowAgentNodeModelFromViewModel(viewModel);
  const config = useAgentNodeConfig(agentId);
  const agentFromModel = useWatchableValue(
    nodeModel?.agentWatchable ?? NULL_AGENT
  );
  const listMeta = useAgentLiveMeta(agentId);
  const agent =
    agentFromModel ??
    (agentId !== undefined ? agentFromNodeConfig(agentId, config) : null);
  const meta = resolveAgentDisplayMeta(agent, config, listMeta);
  return { agent, config, meta };
}
